import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  boundingRect,
  boardTemplateBounds,
  colorForId,
  DEFAULT_FRAME_STYLE,
  DEFAULT_IFRAME_VIEWPORT,
  DEFAULT_PAGE_FACTOR,
  DEFAULT_TEXT_COLOR,
  FRAME_AROUND_PADDING,
  FRAME_PLACEMENT_FILL,
  fitRatio,
  SHARE_MODE_INFO,
  UPLOAD_ACCEPT,
  uploadRejection,
  type FrameFormat,
  type BoardAccessDto,
  type BoardElement,
  type BoardTemplateDto,
  type Frame,
  type MediaDto,
  type Rect,
  type TemplateContent,
  type TemplateDto,
} from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { Link } from '../router.js'
import { useText } from '../i18n.js'
import { CameraStore } from '../canvas/CameraStore.js'
import { renderPreview } from '../canvas/renderPreview.js'
import { Canvas } from '../canvas/Canvas.js'
import { ZoomControls } from '../canvas/ZoomControls.js'
import { selectOne, selectedIds, selectionSize, soleId, type Selection } from '../canvas/selection.js'
import { useCollaboration } from '../collab/useProvider.js'
import { usePresence } from '../collab/usePresence.js'
import {
  addElement,
  createId,
  moveElementLayer,
  nextZ,
  reorderElement,
  removeElement,
  updateElement,
  useBoardElements,
} from '../collab/elements.js'
import {
  addFrame,
  createFrameId,
  moveFrame,
  nextFrameOrder,
  removeFrame,
  reorderFrame,
  updateFrame,
  useBoardFrames,
} from '../collab/frames.js'
import {
  setBoardBackground,
  setBoardTheme,
  useBoardBackground,
  useBoardTheme,
} from '../collab/meta.js'
import { useUndo } from '../collab/useUndo.js'
import { copyElements, duplicateElements, hasClipboard, pasteElements } from '../collab/clipboard.js'
import { usePresentation } from '../presentation/usePresentation.js'
import { useNotes } from '../presentation/useNotes.js'
import { WaitingScreen } from '../presentation/WaitingScreen.js'
import { FollowBar, PresentationBar } from '../presentation/PresentationBar.js'
import { PreviewPanel } from '../presentation/PreviewPanel.js'
import { NotesPanel } from '../presentation/NotesPanel.js'
import { HistoryDialog } from '../components/HistoryDialog.js'
import { FrameMenu } from '../components/FrameMenu.js'
import { useSmallScreen } from './useSmallScreen.js'
import { FramesPanel } from '../components/FramesPanel.js'
import { LayersPanel } from '../components/LayersPanel.js'
import { Participants } from '../components/Participants.js'
import { ThemeDialog } from '../components/ThemeDialog.js'
import { ShareDialog } from '../components/ShareDialog.js'
import { UrlDialog } from '../components/UrlDialog.js'
import { SaveTemplateDialog, TemplatesDialog } from '../components/TemplatesDialog.js'
import { BoardTemplatesDialog } from '../components/BoardTemplatesDialog.js'
import { insertTemplate } from '../collab/templates.js'
import { boardTemplateOrigin, insertBoardTemplate } from '../collab/boardTemplates.js'
import { takeInitialDesign } from '../collab/initialDesign.js'
import { isShowcaseVisit, takeTalkStart } from './visitIntent.js'
import { ShowcaseBar } from '../components/ShowcaseBar.js'
import { useFontStylesheet } from '../fonts.js'
import { useOrgFonts } from '../components/FontLibrary.js'

/** Anteil der Fensterbreite, den ein neu eingefügtes Element einnehmen soll. */
const PLACEMENT_WIDTH_RATIO = 0.4

/**
 * Wo ein Medium hinsoll: an diesem Punkt zentriert oder mit der linken Kante
 * dort.
 */
interface MediaAnchor {
  x: number
  y: number
  align: 'center' | 'left'
}

/** Stabile Referenz, damit das Ausblenden der Frames keinen Rerender erzwingt. */
const EMPTY_FRAMES: Frame[] = []

export function BoardPage({ boardId }: { boardId: string }): React.ReactElement {
  const t = useText()
  const [access, setAccess] = useState<BoardAccessDto | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [mediaList, setMediaList] = useState<MediaDto[]>([])
  const [selection, setSelection] = useState<Selection>(null)
  const [showShare, setShowShare] = useState(false)
  const [showUrlDialog, setShowUrlDialog] = useState(false)
  const [showTheme, setShowTheme] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [showBoardTemplates, setShowBoardTemplates] = useState(false)
  const [templateDraft, setTemplateDraft] = useState<
    { frameName: string; content: TemplateContent } | null
  >(null)
  const [editingIframe, setEditingIframe] = useState<string | null>(null)
  const [uploads, setUploads] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)
  /** Kein Fehler, aber erwähnenswert — etwa ein gekürztes PDF. */
  const [uploadNotice, setUploadNotice] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  /** „Frei ziehen" ist scharf: Der nächste Zug auf dem Board spannt einen Frame auf. */
  const [frameDraw, setFrameDraw] = useState(false)

  const camera = useMemo(() => new CameraStore(), [boardId])

  useEffect(() => {
    let cancelled = false
    api
      .board(boardId)
      .then((data) => {
        if (cancelled) return
        setAccess(data)
        setMediaList(data.media)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(
          error instanceof ApiError && error.status === 404
            ? t('board.notFound')
            : t('board.loadFailed'),
        )
      })
    return () => {
      cancelled = true
    }
  }, [boardId, t])

  const identity = useMemo(
    () =>
      access
        ? { id: access.me.id, name: access.me.displayName, isGuest: access.me.isGuest }
        : { id: 'anonymous', name: t('board.guest'), isGuest: true },
    [access, t],
  )

  const { doc, provider, status } = useCollaboration(boardId, identity)
  const elements = useBoardElements(doc)
  const frames = useBoardFrames(doc)
  const background = useBoardBackground(doc)
  const theme = useBoardTheme(doc)
  const presence = usePresence(provider)
  const presentation = usePresentation(provider, presence, camera, frames)
  // Notizen holt nur, wer das Board bearbeiten darf — Gäste bekommen sie nie.
  const notes = useNotes(boardId, access?.canEdit ?? false)
  const setNote = notes.set
  const mediaById = useMemo(() => new Map(mediaList.map((m) => [m.id, m])), [mediaList])
  const canEdit = access?.canEdit ?? false
  /*
   * Auf dem Telefon wird angesehen, nicht gebaut — siehe `useSmallScreen`.
   * Ausdrücklich mit Ausweg: Wer es trotzdem versuchen will, darf. Eine Sperre
   * ohne Tür wäre Bevormundung; ein Hinweis, den man wegdrücken kann, ist eine
   * Empfehlung.
   */
  const smallScreen = useSmallScreen()
  const [phoneEditing, setPhoneEditing] = useState(false)
  const readOnlyPhone = smallScreen && !phoneEditing

  // Die selbst gehosteten Schriften gehören der Organisation dieses Boards —
  // auch für Gäste, die es nur über den Freigabe-Link kennen.
  useFontStylesheet(access?.board.orgId)
  /*
   * Die Schriften der Organisation — die Textleiste bietet sie zur Wahl an.
   *
   * Nur für Mitglieder: Ein Gast am Freigabe-Link bekommt hier nichts und
   * wählt darum auch keine Schrift aus. Angezeigt bekommt er trotzdem jede,
   * denn das Stylesheet oben steht auch ihm offen.
   */
  const [orgFonts] = useOrgFonts(access?.board.orgId ?? null)

  /*
   * Das im Anlege-Fenster gewählte Design in das frische Dokument schreiben.
   *
   * Es steht bewusst nicht schon beim Anlegen in der Datenbank: Untergrund und
   * Schriften sind Board-Inhalt und liegen im CRDT. `takeInitialDesign` liefert
   * den Entwurf genau einmal — ein zweiter Aufbau derselben Ansicht (etwa nach
   * einem Verbindungsabbruch) überschreibt also nicht, was inzwischen von Hand
   * geändert wurde.
   */
  useEffect(() => {
    if (!canEdit) return
    const initial = takeInitialDesign(boardId)
    if (!initial) return
    setBoardBackground(doc, initial.background)
    setBoardTheme(doc, initial.theme)

    // Das gewählte Gerüst: leere Frames im Ursprung, dort schaut die Kamera
    // beim Öffnen hin. Die Sprechernotizen gehen einzeln nach Postgres — sie
    // liegen nicht im Dokument.
    if (initial.template) {
      for (const frame of insertBoardTemplate(doc, initial.template, { x: 0, y: 0 })) {
        if (frame.notes.trim() !== '') setNote(frame.id, frame.notes)
      }
    }
    // `setNote` statt `notes`: Das Notizobjekt wird bei jedem Tastendruck neu
    // gebaut, die Schreibfunktion nicht.
  }, [boardId, canEdit, doc, setNote])

  /*
   * Im Vortrag wird nicht gearbeitet.
   *
   * Auswahlrahmen, Anfasser und Werkzeugleisten haben dort nichts verloren, und
   * ein versehentliches Verschieben mitten in der Präsentation wäre ärgerlich.
   * Bedienbar bleibt, was zum Zeigen gehört: Frames anfahren, Videos abspielen,
   * eingebettete Websites steuern.
   */
  const editable = canEdit && !presentation.isPresenting && !readOnlyPhone

  /*
   * Medien nachladen, die dieser Client noch nicht kennt.
   *
   * `/api/boards/:id` liefert den Medienstand vom Betreten. Lädt danach jemand
   * anderes ein Bild hoch, kommt zwar das Element über das Yjs-Dokument an, aber
   * nicht der Eintrag mit den Auflösungsstufen — das Element bliebe ein leeres
   * Rechteck. Dasselbe trifft geführte Gäste, die das Dokument erst mit dem
   * Präsentationsstart bekommen.
   */
  const mediaChecked = useRef(new Set<string>())
  useEffect(() => {
    const unbekannt = elements
      .map((element) => ('mediaId' in element ? element.mediaId : null))
      .filter((id): id is string => !!id && !mediaById.has(id) && !mediaChecked.current.has(id))
    if (unbekannt.length === 0) return

    // Einmal je unbekannter ID: sonst würde ein gelöschtes Medium eine
    // Endlosschleife aus Nachladeversuchen auslösen.
    for (const id of unbekannt) mediaChecked.current.add(id)

    let cancelled = false
    const timer = setTimeout(() => {
      api
        .media(boardId)
        .then((list) => {
          if (!cancelled) setMediaList(list)
        })
        .catch(() => {
          // Nächste Änderung am Board versucht es erneut.
        })
    }, 200)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [boardId, elements, mediaById])

  // Beim ersten Laden auf den vorhandenen Inhalt einpassen, damit man nicht im
  // Nichts eines unendlichen Boards landet. Läuft eine Präsentation, gewinnt
  // deren Ansicht — dafür ist der Folgemodus zuständig.
  const didInitialFit = useRef(false)
  useEffect(() => {
    if (didInitialFit.current || presentation.presenter) return
    /*
     * Auf dem Telefon wird auf den ersten Frame eingepasst statt auf alles.
     *
     * Ein ganzes Board auf einer Handbreite heißt drei Prozent Zoom: Man sieht,
     * dass da etwas ist, lesen kann man nichts. Der erste Frame ist das, was der
     * Autor als Anfang gemeint hat — und er füllt den Schirm.
     */
    const bounds =
      smallScreen && frames.length > 0 ? frames[0] : boundingRect([...frames, ...elements])
    if (!bounds) return
    didInitialFit.current = true
    camera.animateToRect(bounds, smallScreen ? 1.02 : 1.15, 0)
  }, [camera, elements, frames, presentation.presenter, smallScreen])

  useUndo(doc, editable)

  const startPresenting = useCallback(() => {
    // Auswahl aufheben: Rahmen und Anfasser sind Autorenwerkzeug und lenken im
    // Vortrag nur ab.
    setSelection(null)
    presentation.start()
  }, [presentation])

  const fitAll = useCallback(() => {
    presentation.breakAway()
    const bounds = boundingRect([...frames, ...elements])
    if (bounds) camera.animateToRect(bounds)
  }, [camera, elements, frames, presentation])

  /*
   * Demo, die im Vortrag beginnt.
   *
   * Wer „selbst vortragen" gewählt hat, soll nicht erst eine Leinwand suchen
   * und den Knopf oben rechts finden müssen — der Vortrag ist der Punkt.
   * Gewartet wird auf die Frames: Das Dokument kommt über die WebSocket-
   * Verbindung und ist beim ersten Bild noch leer, und `presentation.start()`
   * ohne Frames setzte den Fokus auf `null` und zeigte „1/0".
   */
  useEffect(() => {
    if (!canEdit || frames.length === 0) return
    if (!takeTalkStart(boardId)) return
    startPresenting()
  }, [boardId, canEdit, frames.length, startPresenting])

  // --- Frames ---------------------------------------------------------------

  /**
   * Einen Frame anlegen — der gemeinsame Abschluss aller drei Wege.
   *
   * `zoom` steuert, ob die Ansicht dem neuen Frame folgt. Beim gewählten Format
   * ist das richtig (er entsteht mittig im Blickfeld und soll ganz zu sehen
   * sein), beim frei gezogenen und beim um die Auswahl gelegten nicht: Dort hat
   * die Person den Ausschnitt gerade selbst bestimmt, und ihn zu verstellen
   * wäre eine Bevormundung.
   */
  const createFrame = useCallback(
    (rect: Rect, zoom: boolean) => {
      const id = createFrameId()
      addFrame(doc, {
        id,
        name: t('board.frameName', { number: frames.length + 1 }),
        x: rect.x,
        y: rect.y,
        w: rect.w,
        h: rect.h,
        order: nextFrameOrder(doc),
        ...DEFAULT_FRAME_STYLE,
      })
      setSelection(selectOne('frame', id))
      // Etwas herauszoomen: sonst liegt die Beschriftung des gerade angelegten
      // Frames über dem oberen Bildrand und ist weder sicht- noch anfassbar.
      if (zoom) camera.animateToRect(rect, 1.14, 320)
    },
    [camera, doc, frames.length, t],
  )

  /** Gewähltes Format, mittig ins Blickfeld. */
  const addFrameInFormat = useCallback(
    (format: FrameFormat) => {
      createFrame(fitRatio(camera.getViewport(), format.ratio, FRAME_PLACEMENT_FILL), true)
    },
    [camera, createFrame],
  )

  /**
   * Frame um die Auswahl.
   *
   * Der Rand ist relativ zur größeren Kante, nicht absolut: Um ein Logo von
   * 200 Einheiten soll nicht derselbe Rand liegen wie um eine ganze Bildstrecke.
   */
  const addFrameAroundSelection = useCallback(() => {
    // Elemente **und** Frames: Ein Frame um einen vorhandenen Frame zu legen ist
    // zulässig — verschachtelte Frames nehmen ihren Inhalt ohnehin mit.
    const elementIds = new Set(selectedIds(selection, 'element'))
    const frameIds = new Set(selectedIds(selection, 'frame'))
    const chosen: Rect[] = [
      ...elements.filter((element) => elementIds.has(element.id)),
      ...frames.filter((frame) => frameIds.has(frame.id)),
    ]
    const bounds = boundingRect(chosen)
    if (!bounds) return
    const pad = Math.max(bounds.w, bounds.h) * FRAME_AROUND_PADDING
    createFrame(
      { x: bounds.x - pad, y: bounds.y - pad, w: bounds.w + pad * 2, h: bounds.h + pad * 2 },
      false,
    )
  }, [createFrame, elements, frames, selection])

  /**
   * Einen Frame als Vorlage vorbereiten.
   *
   * Mitgenommen wird, was **vollständig** im Frame liegt — dieselbe Regel wie
   * beim Verschieben eines Frames. Etwas halb Überlappendes wäre in der Vorlage
   * angeschnitten, ohne dass man es beim Sichern gesehen hätte.
   */
  const prepareTemplate = useCallback(
    (frame: Frame) => {
      const inside = elements.filter(
        (element) =>
          element.x >= frame.x &&
          element.y >= frame.y &&
          element.x + element.w <= frame.x + frame.w &&
          element.y + element.h <= frame.y + frame.h,
      )
      setTemplateDraft({
        frameName: frame.name,
        content: {
          width: frame.w,
          height: frame.h,
          background: frame.background || null,
          borderColor: frame.borderColor || null,
          borderWidth: frame.borderWidth,
          // Relativ zur Frame-Ecke: erst dadurch lässt sich die Vorlage
          // anderswo absetzen.
          elements: inside
            .slice()
            .sort((a, b) => a.z - b.z)
            .map((element) => ({ ...element, x: element.x - frame.x, y: element.y - frame.y })),
        },
      })
    },
    [elements],
  )

  /** Eine Vorlage mittig in den sichtbaren Ausschnitt setzen. */
  const applyTemplate = useCallback(
    async (template: TemplateDto) => {
      const applied = await api.applyTemplate(boardId, template.id)
      // Die frisch kopierten Medien sofort bekannt machen, sonst zeigt das Board
      // bis zum nächsten Laden leere Rechtecke.
      const copies = Object.values(applied.media)
      if (copies.length > 0) setMediaList((current) => [...current, ...copies])

      const viewport = camera.getViewport()
      const frameId = insertTemplate(
        doc,
        applied.content,
        applied.media,
        template.name,
        {
          x: viewport.x + (viewport.w - applied.content.width) / 2,
          y: viewport.y + (viewport.h - applied.content.height) / 2,
        },
      )
      setSelection(selectOne('frame', frameId))
      camera.animateToRect(
        {
          x: viewport.x + (viewport.w - applied.content.width) / 2,
          y: viewport.y + (viewport.h - applied.content.height) / 2,
          w: applied.content.width,
          h: applied.content.height,
        },
        1.14,
        320,
      )
    },
    [boardId, camera, doc],
  )

  /**
   * Ein Gerüst auf das offene Board setzen.
   *
   * Rechts neben das, was schon da ist, und hinten in die Vortragsreihenfolge —
   * überlagert würde niemand mehr auseinanderbekommen. Anschließend fährt die
   * Kamera hin, sonst passierte das Ganze außerhalb des Bildes.
   */
  const applyBoardTemplate = useCallback(
    async (template: BoardTemplateDto) => {
      const origin = boardTemplateOrigin(doc, template.content)
      const created = insertBoardTemplate(doc, template.content, origin)
      for (const frame of created) {
        if (frame.notes.trim() !== '') setNote(frame.id, frame.notes)
      }

      const bounds = boardTemplateBounds(template.content)
      camera.animateToRect(
        { x: origin.x + bounds.x, y: origin.y + bounds.y, w: bounds.w, h: bounds.h },
        1.14,
        320,
      )
      const first = created[0]
      if (first) setSelection(selectOne('frame', first.id))
    },
    [camera, doc, setNote],
  )

  /**
   * Der Frame, auf dem die Ansicht gerade steht — für die Schrittanzeige auf
   * schmalen Schirmen. Gemerkt statt gemessen: Beim freien Verschieben gibt es
   * keinen „aktuellen" Frame, und ein springender Zähler wäre schlimmer als
   * einer, der stehen bleibt.
   */
  const [currentFrameIndex, setCurrentFrameIndex] = useState(0)

  const zoomToFrame = useCallback(
    (frame: Frame) => {
      // Kein `breakAway` hier: beim Präsentierenden würde das `focus` leeren und
      // sofort neu setzen — für die Gäste sähe der Wechsel dann aus wie ein
      // Beitritt und spränge hart. `goToFrame` entscheidet je nach Rolle selbst.
      setSelection(selectOne('frame', frame.id))
      const index = frames.findIndex((candidate) => candidate.id === frame.id)
      if (index >= 0) {
        setCurrentFrameIndex(index)
        presentation.goToFrame(index)
      } else camera.animateToRect(frame, 1.04)
    },
    [camera, frames, presentation],
  )

  const stepFrame = useCallback(
    (delta: number) => {
      if (frames.length === 0) return
      // Am Ende wird nicht umgebrochen: Ein Sprung von der letzten zur ersten
      // Folie sieht aus wie ein Fehler.
      const ziel = Math.min(frames.length - 1, Math.max(0, currentFrameIndex + delta))
      const frame = frames[ziel]
      if (!frame) return
      setCurrentFrameIndex(ziel)
      camera.animateToRect(frame, 1.02)
    },
    [camera, currentFrameIndex, frames],
  )

  /*
   * Kopieren und Duplizieren.
   *
   * Kopiert wird bewusst nicht in die Systemablage: Ein Board-Element ist weder
   * Text noch Bild, das ein anderes Programm verstünde. Innerhalb von Præsto
   * reicht ein Modulspeicher — und der funktioniert auch zwischen zwei Boards
   * im selben Browser. Das Einfügen hängt dagegen am `paste`-Ereignis, siehe
   * unten: Von außen kommen sehr wohl Dateien.
   */
  useEffect(() => {
    if (!editable) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey)) return
      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      const key = event.key.toLowerCase()
      const chosenIds = selectedIds(selection, 'element')
      const chosen = elements.filter((element) => chosenIds.includes(element.id))

      if (key === 'c' && chosen.length > 0) {
        event.preventDefault()
        copyElements(chosen)
      } else if (key === 'd' && chosen.length > 0) {
        event.preventDefault()
        setSelection({ kind: 'element', ids: duplicateElements(doc, chosen) })
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [camera, doc, editable, elements, selection])


  /*
   * Vorschaubild für die Board-Übersicht.
   *
   * Zeitversetzt nach der letzten Änderung, damit während des Arbeitens nicht
   * ständig gezeichnet und hochgeladen wird. Nur Bearbeitende erzeugen es —
   * Gäste haben nichts beizutragen und in geführten Modi ohnehin kein Dokument.
   */
  useEffect(() => {
    if (!canEdit || elements.length === 0) return
    let cancelled = false
    const timer = setTimeout(() => {
      void renderPreview(elements, frames, mediaById, background, theme)
        .then((blob) => {
          if (blob && !cancelled) return api.uploadPreview(boardId, blob)
          return undefined
        })
        .catch(() => {
          // Eine fehlende Vorschau ist kein Grund, irgendetwas zu unterbrechen.
        })
    }, 6000)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [background, boardId, canEdit, elements, frames, mediaById, theme])

  // Pfeiltasten schalten während der Präsentation durch die Frames. Bewusst
  // nicht die Leertaste — die hält im Canvas die Ansicht zum Verschieben.
  useEffect(() => {
    if (!presentation.isPresenting) return
    const onKeyDown = (event: KeyboardEvent): void => {
      // Nicht nur `INPUT`: `Z` liegt ohne Zusatztaste auf der Tastatur, und ein
      // Kürzel, das beim Tippen zuschlägt, ist schlimmer als keines. Im Vortrag
      // ist zwar nichts bearbeitbar — aber das ist eine Eigenschaft des
      // Zustands, nicht dieser Zeile, und Zustände ändern sich.
      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      if (event.key === 'ArrowRight' || event.key === 'PageDown') {
        event.preventDefault()
        presentation.next()
      }
      if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault()
        presentation.previous()
      }
      // Z wie Zeiger — ohne Zusatztaste, weil im Vortrag eine Hand am
      // Präsentationsgerät hängt und die andere zeigt.
      if (event.key.toLowerCase() === 'z' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault()
        presentation.togglePointer()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [presentation])

  // --- Medien ---------------------------------------------------------------

  /** Der gerade ausgewählte Frame, falls einer ausgewählt ist. */
  const activeFrame = useMemo(
    () => {
      const id = soleId(selection, 'frame')
      return id ? frames.find((f) => f.id === id) : undefined
    },
    [frames, selection],
  )

  /**
   * Zielrechteck für ein neues Element.
   *
   * Ist ein Frame ausgewählt, landet das Element darin und wird auf den Frame
   * skaliert — man rahmt schließlich einen Inhalt ein, den man gleich danach
   * einsetzt. Sonst wie bisher mittig ins Blickfeld, dort aber nie größer als
   * das Original.
   */
  const placementRect = useCallback(
    (aspect: number): Rect => {
      const area: Rect = activeFrame ?? camera.getViewport()
      return fitRatio(area, aspect, activeFrame ? FRAME_PLACEMENT_FILL : PLACEMENT_WIDTH_RATIO)
    },
    [activeFrame, camera],
  )

  /** Legt ein Medium ab und gibt zurück, wo es gelandet ist. */
  const placeMedia = useCallback(
    (media: MediaDto, anchor?: MediaAnchor): Rect => {
      const aspect = media.width / media.height
      const rect = placementRect(aspect)
      // Außerhalb eines Frames nicht über die Originalgröße hinaus vergrößern.
      if (!activeFrame && rect.w > media.width) {
        rect.w = media.width
        rect.h = media.width / aspect
        rect.x += (placementRect(aspect).w - rect.w) / 2
      }
      if (anchor) {
        // `left` heißt: Die linke Kante liegt hier. Nur so entsteht eine Reihe
        // ohne Lücken und ohne Überschneidung, auch wenn die Seiten
        // unterschiedlich breit sind — bei `center` müsste der Aufrufer die
        // Breite des *nächsten* Bildes schon kennen.
        rect.x = anchor.align === 'left' ? anchor.x : anchor.x - rect.w / 2
        rect.y = anchor.y - rect.h / 2
      }
      addElement(doc, {
        id: createId(),
        type: media.kind,
        x: rect.x,
        y: rect.y,
        w: rect.w,
        h: rect.h,
        rotation: 0,
        z: nextZ(doc),
        mediaId: media.id,
        naturalW: media.width,
        naturalH: media.height,
      })
      return rect
    },
    [activeFrame, doc, placementRect],
  )

  const addText = useCallback(() => {
    const area: Rect = activeFrame ?? camera.getViewport()
    const w = area.w * (activeFrame ? 0.8 : 0.45)
    // Schriftgröße aus der Fläche ableiten: eine Überschrift soll im Frame wie
    // eine Überschrift wirken, nicht wie ein Fleck oder wie Kleingedrucktes.
    const fontSize = Math.max(12, Math.round(area.w / 22))
    const id = createId()
    addElement(doc, {
      id,
      type: 'text',
      x: area.x + area.w / 2 - w / 2,
      y: area.y + area.h / 2 - fontSize,
      w,
      h: Math.round(fontSize * 1.25),
      rotation: 0,
      z: nextZ(doc),
      text: '',
      fontSize,
      color: DEFAULT_TEXT_COLOR,
      align: activeFrame ? 'center' : 'left',
      bold: false,
      serif: false,
      autoHeight: true,
      valign: 'top',
    })
    setSelection(selectOne('element', id))
  }, [activeFrame, camera, doc])

  /** Element per Ebenenliste einem Frame zuordnen: es wird tatsächlich hineinbewegt. */
  const assignToFrame = useCallback(
    (elementId: string, frameId: string) => {
      const element = elements.find((candidate) => candidate.id === elementId)
      const frame = frames.find((candidate) => candidate.id === frameId)
      if (!element || !frame) return

      const aspect = element.w / element.h
      let w = Math.min(element.w, frame.w * 0.86)
      let h = w / aspect
      const maxH = frame.h * 0.86
      if (h > maxH) {
        h = maxH
        w = h * aspect
      }
      updateElement(doc, elementId, {
        x: frame.x + frame.w / 2 - w / 2,
        y: frame.y + frame.h / 2 - h / 2,
        w,
        h,
      })
      setSelection(selectOne('element', elementId))
    },
    [doc, elements, frames],
  )

  const uploadFiles = useCallback(
    async (files: File[], boardX?: number, boardY?: number) => {
      setUploadError(null)
      setUploadNotice(null)

      // Was hier schon durchfällt, muss gar nicht erst über die Leitung: Bei
      // einem 400-MB-Video dauerte die Absage sonst so lange wie der Upload.
      const rejected = files.map((file) => uploadRejection(file, t))
      const accepted = files.filter((_, index) => rejected[index] === null)
      const firstProblem = rejected.find((problem) => problem !== null)
      if (firstProblem) setUploadError(firstProblem)
      if (accepted.length === 0) return

      setUploads((count) => count + accepted.length)
      // Von hier aus wächst die Reihe nach rechts: Ein PDF wird zu vielen
      // Seiten, und die sollen nebeneinanderliegen statt aufeinander.
      let anchor: MediaAnchor | undefined =
        boardX !== undefined && boardY !== undefined
          ? { x: boardX, y: boardY, align: 'center' }
          : undefined

      // Nacheinander statt parallel: der Server skaliert jedes Bild in bis zu
      // vier Stufen, gleichzeitige Uploads würden ihn nur ausbremsen.
      for (const file of accepted) {
        try {
          const result = await api.upload(boardId, file)
          setMediaList((current) => [...current, ...result.media])
          if (result.notice) setUploadNotice(result.notice)
          for (const media of result.media) {
            const rect = placeMedia(media, anchor)
            // Die nächste Seite beginnt rechts daneben, mit einem schmalen Steg.
            anchor = { x: rect.x + rect.w * 1.06, y: rect.y + rect.h / 2, align: 'left' }
          }
        } catch (error) {
          setUploadError(
            error instanceof ApiError ? error.message : t('board.uploadFailed', { name: file.name }),
          )
        } finally {
          setUploads((count) => count - 1)
        }
      }
    },
    [boardId, placeMedia, t],
  )

  /*
   * Einfügen.
   *
   * Hängt am `paste`-Ereignis und nicht an Strg+V, weil erst dieses verrät, was
   * in der Systemablage liegt. Ein Bildschirmfoto oder eine aus Figma kopierte
   * Grafik kommt hier als Datei an und wird hochgeladen; sonst gilt der
   * Modulspeicher mit den zuletzt kopierten Elementen. Beides am Tastendruck zu
   * entscheiden ginge nicht — dort ist die Ablage noch unbekannt, und es
   * entstünden bei jedem Bild zusätzlich die alten Elemente.
   */
  useEffect(() => {
    if (!editable) return
    const onPaste = (event: ClipboardEvent): void => {
      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return
      }
      const viewport = camera.getViewport()
      const middle = { x: viewport.x + viewport.w / 2, y: viewport.y + viewport.h / 2 }

      const files = [...(event.clipboardData?.files ?? [])]
      if (files.length > 0) {
        event.preventDefault()
        void uploadFiles(files, middle.x, middle.y)
        return
      }
      if (hasClipboard()) {
        event.preventDefault()
        const ids = pasteElements(doc, middle)
        if (ids.length > 0) setSelection({ kind: 'element', ids })
      }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [camera, doc, editable, uploadFiles])

  const addIframe = useCallback(
    (url: string) => {
      const rect = placementRect(16 / 10)
      addElement(doc, {
        id: createId(),
        type: 'iframe',
        x: rect.x,
        y: rect.y,
        w: rect.w,
        h: rect.h,
        rotation: 0,
        z: nextZ(doc),
        url,
        viewportWidth: DEFAULT_IFRAME_VIEWPORT,
        pageFactor: DEFAULT_PAGE_FACTOR,
        scrollY: 0,
      })
      setShowUrlDialog(false)
    },
    [doc, placementRect],
  )

  // --- Darstellung ----------------------------------------------------------

  if (loadError) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <h1 className="wordmark">Præsto</h1>
          <p className="form-error">{loadError}</p>
          <Link to="/boards" className="button ghost">
            {t('board.toOverview')}
          </Link>
        </div>
      </div>
    )
  }

  if (!access) {
    return (
      <div className="auth-screen">
        <p className="muted">{t('board.loading')}</p>
      </div>
    )
  }

  const isWatching = !!presentation.presenter && !presentation.isPresenting
  // Wer einer Präsentation folgt, soll den Inhalt sehen — nicht die Werkzeuge.
  const chromeHidden = isWatching && presentation.following

  /*
   * Freigabe-Modus des Gastes.
   *
   * `explore` verhält sich wie ein stiller Mitleser mit voller Bewegungsfreiheit.
   * `guided` und `locked` zeigen vor Beginn der Präsentation gar nichts — das
   * erzwingt der Server, indem er das Dokument zurückhält; hier kommt nur die
   * passende Erklärung dazu. `locked` sperrt zusätzlich jede eigene Navigation.
   */
  const modeInfo = access.guestMode ? SHARE_MODE_INFO[access.guestMode] : null
  const awaitingPresentation = !!modeInfo && !modeInfo.contentBeforePresentation && !presentation.presenter
  /*
   * Wann ist die Ansicht festgesetzt?
   *
   * Bei streng geführten Gästen immer, solange jemand präsentiert — und beim
   * Präsentierenden selbst, solange er das Schloss nicht geöffnet hat.
   */
  const viewLocked =
    (!!modeInfo && !modeInfo.freeMovement && !!presentation.presenter) ||
    (presentation.isPresenting && !presentation.freeNavigation)
  const guestCanNavigate = !modeInfo || modeInfo.mode === 'explore'

  // Nur auf dem Vorführ-Board, nie im Vortrag eines Kunden.
  const showcase = isShowcaseVisit(boardId)

  if (awaitingPresentation && access.guestMode) {
    return (
      <>
        {showcase && <ShowcaseBar />}
        <WaitingScreen
          boardTitle={access.board.title}
          mode={access.guestMode}
          waiting={access.waiting}
        />
      </>
    )
  }
  const activeFrameId =
    soleId(selection, 'frame') ??
    (presentation.isPresenting ? (frames[presentation.frameIndex]?.id ?? null) : null)

  return (
    <div className={`board-shell${chromeHidden ? ' is-watching' : ''}`}>
      {/* Bleibt auch sichtbar, während der Rest der Oberfläche im Folgemodus
          verschwindet — sie ist der einzige Weg aus der Vorführung heraus. */}
      {showcase && <ShowcaseBar />}
      <Canvas
        camera={camera}
        doc={doc}
        provider={provider}
        elements={elements}
        // Immer alle Frames: gestaltete Frames sind Inhalt und werden jedem
        // gezeigt, die Werkzeugschicht blendet `FrameView` selbst aus.
        frames={frames}
        mediaById={mediaById}
        presence={presence}
        canEdit={editable}
        selection={presentation.isPresenting ? null : selection}
        onSelect={setSelection}
        onDropFiles={(files, x, y) => void uploadFiles(files, x, y)}
        onUserCameraInput={viewLocked ? undefined : presentation.breakAway}
        background={background}
        theme={theme}
        fonts={orgFonts}
        // Im Vortrag und bei geführten Zuschauern gibt es kein Ziehen — dann
        // braucht die eingebettete Seite auch keine Fangschicht davor.
        // Nur streng geführte Gäste dürfen die Seite gar nicht bedienen. Der
        // Präsentierende soll sie gerade im Vortrag steuern können, obwohl
        // seine Ansicht dort festgesetzt ist.
        allowIframeInteraction={!modeInfo || modeInfo.freeMovement}
        // Nicht `editable`: Im Vortrag ist das Bearbeiten aus, das Schreibrecht
        // bleibt. Sonst scrollte der Vortragende nur für sich.
        canWrite={canEdit}
        onEditIframeUrl={setEditingIframe}
        onSaveFrameTemplate={editable ? prepareTemplate : undefined}
        viewLocked={viewLocked}
        frameDraw={editable && frameDraw}
        onFrameDrawn={(rect) => {
          setFrameDraw(false)
          createFrame(rect, false)
        }}
        onFrameDrawCancel={() => setFrameDraw(false)}
      />

      {!chromeHidden && (
        <header className="board-bar">
          <div className="board-bar-left">
            <Link to="/boards" className="wordmark small" title={t('board.toOverview')}>
              Præsto
            </Link>
            <span className="board-title">{access.board.title}</span>
            <span
              className={`status status-${status}`}
              title={t('board.connection', { status })}
            />
          </div>

          <div className="board-bar-right">
            <Participants
              presence={presence}
              me={{
                name: access.me.displayName,
                color: colorForId(access.me.id),
                isGuest: access.me.isGuest,
                isPresenting: presentation.isPresenting,
              }}
            />

            {/* Auf dem Telefon bleibt die Werkzeugseite der Kopfleiste weg:
                Verlauf, Design und Vortrag sind Arbeit am Board, und die findet
                dort nicht statt. Sie kommen mit „Trotzdem bearbeiten" zurück. */}
            {canEdit && !presentation.isPresenting && !readOnlyPhone && (
              <>
                <button
                  className="button ghost icon"
                  onClick={() => setShowHistory(true)}
                  title={t('board.historyTitle')}
                  aria-label={t('history.title')}
                >
                  ↺
                </button>
                <button
                  className="button ghost icon"
                  onClick={() => setShowTheme(true)}
                  title={t('board.themeTitle')}
                  aria-label={t('boards.design')}
                >
                  ◐
                </button>
                <button className="button ghost" onClick={() => setShowShare(true)}>
                  {t('board.share')}
                </button>
                <button className="button primary" onClick={startPresenting}>
                  {t('board.present')}
                </button>
              </>
            )}
            {!canEdit && <span className="badge">{t('board.viewOnly')}</span>}
          </div>
        </header>
      )}

      {(canEdit || guestCanNavigate) && !chromeHidden && frames.length > 0 && (
        <FramesPanel
          frames={frames}
          activeId={activeFrameId}
          canEdit={editable}
          addControl={
            <FrameMenu
              onFormat={addFrameInFormat}
              onDraw={() => setFrameDraw(true)}
              onAround={addFrameAroundSelection}
              hasSelection={selectionSize(selection) > 0}
              direction="down"
              compact
            />
          }
          onSelect={zoomToFrame}
          onRename={(id, name) => updateFrame(doc, id, { name })}
          onMove={(id, delta) => moveFrame(doc, frames, id, delta)}
          onReorder={(id, beforeId) => reorderFrame(doc, frames, id, beforeId)}
          onRemove={(id) => {
            removeFrame(doc, id)
            setSelection(null)
          }}
          notes={editable ? notes : undefined}
        />
      )}

      {editable && !chromeHidden && (
        <LayersPanel
          elements={elements}
          frames={frames}
          mediaById={mediaById}
          selection={selection}
          onSelect={setSelection}
          onMoveLayer={(id, delta) => moveElementLayer(doc, elements, id, delta)}
          onReorder={(id, beforeId) => reorderElement(doc, elements, id, beforeId)}
          onRemove={(target) => {
            // Löscht die ganze Auswahl, nicht nur die angeklickte Zeile: Die
            // Liste zeigt sie als eine Auswahl, also verhält sie sich auch so.
            doc.transact(() => {
              for (const id of target.ids) {
                if (target.kind === 'element') removeElement(doc, id)
                else removeFrame(doc, id)
              }
            })
            setSelection(null)
          }}
          onAssignToFrame={assignToFrame}
        />
      )}

      {presentation.isPresenting && (
        <>
          <PresentationBar
            presentation={presentation}
            frames={frames}
            viewerCount={presentation.viewers.length}
            followerCount={presentation.viewers.filter((v) => v.following).length}
          />
          <PreviewPanel
            viewers={presentation.viewers}
            elements={elements}
            // Ohne Frames: die Vorschau soll zeigen, was der Gast sieht — und
            // der sieht die Autorenstruktur nicht.
            frames={EMPTY_FRAMES}
            mediaById={mediaById}
            background={background}
          />
          {canEdit && (
            <NotesPanel notes={notes} frames={frames} frameIndex={presentation.frameIndex} />
          )}
        </>
      )}

      {isWatching && <FollowBar presentation={presentation} />}

      {/*
        Einfügen gehört nicht in dieselbe Leiste wie Freigeben und Präsentieren:
        Das eine bringt Inhalte aufs Board, das andere tut etwas mit dem ganzen
        Board. Getrennt muss man nicht mehr lesen, um das Richtige zu finden.
      */}
      {canEdit && readOnlyPhone && !chromeHidden && !presentation.isPresenting && (
        <div className="small-screen-note">
          <span>{t('board.smallScreen')}</span>
          <button className="button ghost" onClick={() => setPhoneEditing(true)}>
            {t('board.smallScreenAnyway')}
          </button>
        </div>
      )}

      {editable && !chromeHidden && (
        <div className="insert-bar">
          <button className="insert-tool" onClick={() => fileInputRef.current?.click()}>
            <span className="insert-icon">▣</span>
            {t('board.insert.media')}
          </button>
          <button className="insert-tool" onClick={() => setShowUrlDialog(true)}>
            <span className="insert-icon">◱</span>
            {t('board.insert.website')}
          </button>
          <button className="insert-tool" onClick={addText}>
            <span className="insert-icon">T</span>
            {t('board.insert.text')}
          </button>
          <span className="insert-divider" />
          <FrameMenu
            onFormat={addFrameInFormat}
            onDraw={() => setFrameDraw(true)}
            onAround={addFrameAroundSelection}
            hasSelection={selectionSize(selection) > 0}
          />
          <button
            className="insert-tool"
            onClick={() => setShowTemplates(true)}
            title={t('board.insert.templateTitle')}
          >
            <span className="insert-icon">❐</span>
            {t('board.insert.template')}
          </button>
          <button
            className="insert-tool"
            onClick={() => setShowBoardTemplates(true)}
            title={t('board.insert.boardTemplateTitle')}
          >
            <span className="insert-icon">⊞</span>
            {t('board.insert.boardTemplate')}
          </button>
        </div>
      )}

      {!chromeHidden && !presentation.isPresenting && (
        <div className="board-foot">
          <ZoomControls camera={camera} onFit={fitAll} />
          {/* Der Ersatz für die Frame-Liste, die auf schmalen Schirmen
              ausgeblendet ist: Ohne sie käme man auf dem Telefon von Frame zu
              Frame nur durch Zielen mit zwei Fingern. */}
          {smallScreen && frames.length > 1 && (
            <div className="frame-steps">
              <button
                className="button ghost icon"
                onClick={() => stepFrame(-1)}
                aria-label={t('board.frameBack')}
              >
                ‹
              </button>
              <span className="frame-steps-label">
                {t('board.frameOf', {
                  index: Math.max(1, currentFrameIndex + 1),
                  total: frames.length,
                })}
              </span>
              <button
                className="button ghost icon"
                onClick={() => stepFrame(1)}
                aria-label={t('board.frameForward')}
              >
                ›
              </button>
            </div>
          )}
          <span className="hint">{t('board.panHint')}</span>
        </div>
      )}

      {frameDraw && <div className="toast">{t('frame.draw.hint')}</div>}
      {uploads > 0 && (
        <div className="toast">
          {t.plural(uploads, 'board.uploading.one', 'board.uploading.many')}
        </div>
      )}
      {uploadError && (
        <div className="toast error" onClick={() => setUploadError(null)}>
          {uploadError}
        </div>
      )}
      {uploadNotice && (
        <div className="toast" onClick={() => setUploadNotice(null)}>
          {uploadNotice}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={UPLOAD_ACCEPT}
        multiple
        hidden
        onChange={(event) => {
          const files = [...(event.target.files ?? [])]
          event.target.value = ''
          if (files.length > 0) void uploadFiles(files)
        }}
      />

      {showTheme && (
        <ThemeDialog
          theme={theme}
          background={background}
          orgId={access.board.orgId}
          onChange={(next) => setBoardTheme(doc, next)}
          onChangeBackground={(color) => setBoardBackground(doc, color)}
          onClose={() => setShowTheme(false)}
        />
      )}
      {showHistory && (
        <HistoryDialog
          boardId={boardId}
          doc={doc}
          mediaById={mediaById}
          background={background}
          onClose={() => setShowHistory(false)}
          onRestored={() => setSelection(null)}
        />
      )}
      {showTemplates && (
        <TemplatesDialog
          orgId={access.board.orgId}
          onApply={applyTemplate}
          onClose={() => setShowTemplates(false)}
        />
      )}
      {showBoardTemplates && (
        <BoardTemplatesDialog
          orgId={access.board.orgId}
          me={access.me}
          boardFrames={frames}
          notes={notes.get}
          onApply={applyBoardTemplate}
          onClose={() => setShowBoardTemplates(false)}
        />
      )}

      {templateDraft && (
        <SaveTemplateDialog
          boardId={boardId}
          frameName={templateDraft.frameName}
          content={templateDraft.content}
          me={access.me}
          onClose={() => setTemplateDraft(null)}
          onSaved={() => setTemplateDraft(null)}
        />
      )}

      {showShare && (
        <ShareDialog
          boardId={boardId}
          canManage={access.canManage}
          onClose={() => setShowShare(false)}
        />
      )}
      {showUrlDialog && <UrlDialog onSubmit={addIframe} onClose={() => setShowUrlDialog(false)} />}
      {editingIframe && (
        <UrlDialog
          initial={elements.find((e) => e.id === editingIframe && e.type === 'iframe')?.type === 'iframe'
            ? (elements.find((e) => e.id === editingIframe) as { url: string }).url
            : undefined}
          onSubmit={(url) => {
            updateElement(doc, editingIframe, { url })
            setEditingIframe(null)
          }}
          onClose={() => setEditingIframe(null)}
        />
      )}
    </div>
  )
}
